import { create } from 'zustand';

export interface AikoMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  text: string;
  toolName?: string;
  toolArgs?: unknown;
  toolResult?: unknown;
  pending?: boolean;
  error?: string;
}

interface AikoState {
  isOpen: boolean;
  messages: AikoMessage[];
  busy: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  ask: (text: string) => void;
  reset: () => void;
  _append: (m: AikoMessage) => void;
  _patchLast: (patch: Partial<AikoMessage>) => void;
  _setBusy: (b: boolean) => void;
}

export const useAiko = create<AikoState>((set, get) => ({
  isOpen: false,
  messages: [],
  busy: false,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  reset: () => set({ messages: [] }),
  _append: (m) => set((s) => ({ messages: [...s.messages, m] })),
  _patchLast: (patch) => set((s) => {
    if (!s.messages.length) return s;
    const copy = [...s.messages];
    copy[copy.length - 1] = { ...copy[copy.length - 1], ...patch };
    return { messages: copy };
  }),
  _setBusy: (b) => set({ busy: b }),
  ask: async (text) => {
    if (!text.trim() || get().busy) return;
    get().open();
    get()._append({ id: crypto.randomUUID(), role: 'user', text });
    get()._append({ id: crypto.randomUUID(), role: 'assistant', text: '', pending: true });
    get()._setBusy(true);

    try {
      const r = await fetch('/api/aiko/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ messages: get().messages.filter((m) => m.role !== 'tool').map((m) => ({ role: m.role, content: m.text })) }),
      });
      if (!r.ok || !r.body) {
        const errText = await r.text().catch(() => 'request failed');
        get()._patchLast({ pending: false, text: '', error: `Aiko backend not reachable. ${errText.slice(0, 240)}` });
        return;
      }
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let acc = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        // The server emits newline-delimited JSON events
        const lines = acc.split('\n');
        acc = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === 'text') {
              get()._patchLast({ pending: true, text: (get().messages.at(-1)?.text ?? '') + ev.delta });
            } else if (ev.type === 'tool_call') {
              get()._append({ id: crypto.randomUUID(), role: 'tool', text: '', toolName: ev.name, toolArgs: ev.args, pending: true });
            } else if (ev.type === 'tool_result') {
              get()._patchLast({ pending: false, toolResult: ev.result, text: summarize(ev.name, ev.result) });
              get()._append({ id: crypto.randomUUID(), role: 'assistant', text: '', pending: true });
            } else if (ev.type === 'error') {
              get()._patchLast({ pending: false, error: ev.message });
            }
          } catch { /* incomplete chunk, ignore */ }
        }
      }
      get()._patchLast({ pending: false });
    } catch (e) {
      get()._patchLast({ pending: false, error: e instanceof Error ? e.message : String(e) });
    } finally {
      get()._setBusy(false);
    }
  },
}));

function summarize(tool: string, result: unknown): string {
  if (!result || typeof result !== 'object') return '';
  const r = result as any;
  if (Array.isArray(r?.data)) return `${tool}: ${r.data.length} rows`;
  if (Array.isArray(r)) return `${tool}: ${r.length} items`;
  if (r?.counts) return `${tool}: ${r.counts.available} available, ${r.counts.unavailable} on rent, ${r.counts.underRepair} in repair`;
  return `${tool}: ok`;
}
